import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { Store } from '@ngrx/store';
import { isObject, toLower } from 'lodash-es';
import { filter, take } from 'rxjs/operators';
import { AppState } from 'src/app/app.state';

import { ApiRequestType } from 'src/app/shared/enums/api.enums';
import { validArray } from 'src/app/shared/utilities/types.utils';
import { postApiRequest } from '../api/api.actions';
import { selectLocales } from './locale.selectors';
import { Locales, LocaleState } from './locale.state';

export const DEFAULT_SECTIONS: string[] = [
	'app',
	'common',
	'summary',
	'error',
	'login',
	'accessologin',
	'pagetitle',
	'legal',
	'nav',
	'button',
	'errorcode',
	'swaplanguage',
];

@Injectable({
	providedIn: 'root',
})
export class LocaleResolve {
	constructor(private store: Store<AppState>) {}

	/**
	 * This function is always called when this is used as a route resolve. Makes sure we load all locales
	 * prior to the page rendering.
	 * @param route The route snapshot.
	 * @param state The router state snapshot.
	 */
	resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<void> {
		return new Promise((resolve) => {
			let requestedLocales: string[] = route.data.locales;
			if (!validArray(requestedLocales)) {
				requestedLocales = [];
			}

			// always ensure each route resolve has the following locales
			requestedLocales = requestedLocales.concat(DEFAULT_SECTIONS).reduce((acc: string[], section: string) => {
				if (!acc.includes(section)) {
					acc.push(section);
				}
				return acc;
			}, []);

			requestedLocales = requestedLocales.map(toLower);

			this.store
				.select(selectLocales)
				.pipe(take(1))
				.subscribe((initialState: LocaleState) => {
					const locales: Locales = requestedLocales.reduce((missing: Locales, section: string): Locales => {
						if (isObject(initialState.locales[section])) {
							missing[section] = { ...initialState.locales[section] };
						}
						return missing;
					}, {});

					const missingLocales = requestedLocales.reduce((missing: string[], section) => {
						if (!isObject(locales[section])) {
							missing.push(section);
						}
						return missing;
					}, []);

					if (!missingLocales?.length) {
						return resolve();
					} else {
						this.store
							.select(selectLocales)
							.pipe(
								filter((localeState: LocaleState) => {
									return missingLocales.every((missingSection) =>
										isObject(localeState.locales[missingSection])
									);
								}),
								take(1)
							)
							.subscribe(() => {
								resolve();
							});

						this.store.dispatch(
							postApiRequest({
								requestType: ApiRequestType.GET_APPLICATION_LOCALE,
								params: {
									section_list: missingLocales.join(','),
								},
								useGlobal: route.data.useGlobal,
							})
						);
					}
				});
		});
	}
}
